import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { StockTransferDataTable } from './StockTransferDataTable';
import { columns, editStockTransferEvent, deleteStockTransferEvent } from './columns';
import { 
  listStockTransferOrders, 
  deleteStockTransferOrder 
} from '@/app/services/stockTransferService';
import { StockTransferOrderListItem } from '@/app/types/stockTransfer';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Define the sort direction type
type SortDirection = 'asc' | 'desc';

const InternalTransfersListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get URL params with defaults
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialSearchQuery = searchParams.get('search') || '';
  const initialSortField = searchParams.get('sort_by') || 'transaction_date';
  const initialSortDir = searchParams.get('sort_dir') || 'desc';
  
  // State for search, pagination, sorting, and dialogs
  const [search, setSearch] = useState<string>(initialSearchQuery);
  const [page, setPage] = useState<number>(initialPage);
  const [limit] = useState<number>(10);
  const [sorting, setSorting] = useState<SortingState>([
    { id: initialSortField, desc: initialSortDir === 'desc' },
  ]);
  const [filters, setFilters] = useState<Record<string, string | number | boolean | { from?: string; to?: string }>>({});
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<StockTransferOrderListItem | null>(null);

  // Update URL when params change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (page > 0) params.set('page', page.toString());
    if (search) params.set('search', search);
    
    // Get current sorting
    const currentSort = sorting[0];
    if (currentSort) {
      params.set('sort_by', currentSort.id);
      params.set('sort_dir', currentSort.desc ? 'desc' : 'asc');
    }
    
    // Add filter params to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(`filter_${key}`, String(value));
      }
    });
    
    setSearchParams(params);
  }, [page, search, sorting, filters, setSearchParams]);

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return { 
      sort_by: 'transaction_date', 
      sort_dir: 'desc' as SortDirection 
    };
    
    const sort = sorting[0];
    const direction: SortDirection = sort.desc ? 'desc' : 'asc';
    
    return {
      sort_by: sort.id,
      sort_dir: direction,
    };
  }, [sorting]);

  // Fetch stock transfer orders with pagination, sorting, and filters
  const { 
    data: transfersData, 
    isLoading, 
    isError, 
    error 
  } = useQuery<ExtendedPaginatedResponse<StockTransferOrderListItem>>({
    queryKey: ['stock-transfer-orders', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;
      
      // Build query parameters
      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };
      
      // Add search if provided
      if (search) {
        params.search_query = search;
      }
      
      // Add filters if any
      if (Object.keys(filters).length > 0) {
        // Convert filters to backend format
        const filterArray: any[] = [];
        Object.entries(filters).forEach(([field, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            filterArray.push({
              field,
              value,
              op: 'eq'
            });
          }
        });
        params.filters = JSON.stringify(filterArray);
      }
      
      return listStockTransferOrders(params);
    },
    enabled: !!currentOrganizationId,
  });

  // Calculate page count from total items and limit if not directly provided by API
  const pageCount = transfersData?.page_count ?? transfersData?.pageCount ??
                   transfersData?.total_pages ?? transfersData?.totalPages ??
                   (transfersData ? Math.ceil(transfersData.total / limit) : 0);

  // Delete mutation
  const deleteTransferMutation = useMutation({
    mutationFn: (id: number) => deleteStockTransferOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer-orders'] });
      setIsDeleteAlertOpen(false);
      setSelectedTransfer(null);
      toast.success('Stock transfer order deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete stock transfer order: ${error.message}`);
    },
  });

  // Event handlers for table actions
  useEffect(() => {
    const handleEditTransfer = (event: CustomEvent<{ transfer: StockTransferOrderListItem }>): void => {
      navigate(`/items/internal-transfers/detail/${event.detail.transfer.id}`);
    };

    const handleDeleteTransfer = (event: CustomEvent<{ transfer: StockTransferOrderListItem }>): void => {
      setSelectedTransfer(event.detail.transfer);
      setIsDeleteAlertOpen(true);
    };

    document.addEventListener(editStockTransferEvent, handleEditTransfer as EventListener);
    document.addEventListener(deleteStockTransferEvent, handleDeleteTransfer as EventListener);

    return () => {
      document.removeEventListener(editStockTransferEvent, handleEditTransfer as EventListener);
      document.removeEventListener(deleteStockTransferEvent, handleDeleteTransfer as EventListener);
    };
  }, [navigate]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0); // Reset to the first page on new search
  }, []);

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0); // Reset to first page on sort change
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    const processedFilters: Record<string, string | number | boolean | { from?: string; to?: string }> = {};
    
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object' && 'from' in value) {
          processedFilters[key] = value as { from?: string; to?: string };
        } else {
          processedFilters[key] = value as string | number | boolean;
        }
      }
    });
    
    setFilters(processedFilters);
    setPage(0); // Reset to first page when filters change
  }, []);

  // Handle delete confirmation
  const handleDeleteTransferConfirm = (): void => {
    if (selectedTransfer) {
      deleteTransferMutation.mutate(selectedTransfer.id);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Internal Stock Transfers</h1>
        <Link to="/items/internal-transfers/detail">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Transfer
          </Button>
        </Link>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading stock transfer orders: {(error as Error)?.message || 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
      )}

      {/* Stock Transfer Data Table */}
      <StockTransferDataTable
        data={transfersData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        searchValue={search}
        onSearchChange={handleSearchChange}
      />

      {/* Delete Confirmation Alert */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the stock transfer order &quot;{selectedTransfer?.document_number}&quot;. 
              This action cannot be undone.
              {selectedTransfer?.status !== 'draft' && (
                <span className="block mt-2 text-destructive">
                  Warning: This transfer is not in draft status. Deleting it may affect stock levels.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTransferConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTransferMutation.isPending}
            >
              {deleteTransferMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InternalTransfersListPage;